import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useSearchParams } from 'react-router'

import { EMPTY_OBJECT } from '../../constants/common'
import { NEWSBOARD_ENTITY } from '../../constants/entity'
import { MENU_POST_ITEM } from '../../constants/menu'
import { ROUTE_UI_SECTION_NAME, SUBMENU_PARAM_KEY } from '../../constants/route'
import { usePrevious, useScreen, useSectionPermissions } from '../../hooks'
import { FilterBar } from '../Filter'
import { NEWSBOARD_WELCOME_CUSTOM_POST_TYPE } from '../Newsboard'
import { PageActions, PageLoader, PageWithWidgets } from '../Page'
import { PageActionsButtonAddEntity } from '../PageActionsButtonAddEntity'
import { getEmptyStateActions } from '../PageEmptyState'
import { PageHeader } from '../PageHeader'
import {
  getPostListItemsData,
  POST_LIST_API_PAGE_SIZE,
  PostListContainer,
  PostListLayout,
  useCreatePost,
  usePostListPage,
} from '../Post'
import { TabScrollList } from '../Tab'

import {
  CREATE_POST_ACTION_NAME,
  EXCLUDE_FILTERS,
  POST_LIST_TAB_NEWSBOARD,
  QA_NAME_NEWSBOARD_PAGE,
  QA_NAME_POST_ADD_NEW,
} from './Posts.constants'
import { useWelcomePost } from './Posts.hooks'
import {
  filterItems,
  getPermanentFilters,
  getPostsTabs,
  parsePermanentFilters,
} from './Posts.utils'
import { PostsContextProvider } from './PostsContextProider'
import { PostsWidgetList } from './PostsWidgetList'

import * as styles from './Posts.module.css'

function Posts() {
  const { t } = useTranslation()

  const { isMobileScreenSize } = useScreen()

  const [modal, setModal] = useState(null)
  const [activeTab, setActiveTab] = useState(POST_LIST_TAB_NEWSBOARD)
  const [initialized, setInitialized] = useState(false)
  const [permissions, setPermissions] = useState(EMPTY_OBJECT)

  const [searchParams, setSearchParams] = useSearchParams()
  const sub = searchParams.get(SUBMENU_PARAM_KEY)

  const {
    handleHideWelcomePost,
    handleLoadWelcomePost,
    hideWelcomePost,
    welcomePost,
  } = useWelcomePost()

  useEffect(() => {
    if (sub) {
      // eslint-disable-next-line react-hooks/set-state-in-effect -- applies route/tab state and resets before reload
      setActiveTab(sub)
      setSearchParams({}, { replace: true })
    }
  }, [sub])

  function getPostListDataItems(data) {
    const items = getPostListItemsData(data)

    if (
      items.length < POST_LIST_API_PAGE_SIZE &&
      !hideWelcomePost &&
      welcomePost?.post_type
    ) {
      return [...items, welcomePost]
    } else {
      return items
    }
  }

  const listLocalFilters = hideWelcomePost
    ? { post_type: NEWSBOARD_WELCOME_CUSTOM_POST_TYPE }
    : null

  const listPage = usePostListPage({
    getDataItems: getPostListDataItems,
    getPermanentFilters,
    listFilters: listLocalFilters,
    listPreloader: handleLoadWelcomePost,
    pageTitle: t('BrowserTab.PostsPageTitleKey'),
    sub: activeTab,
    onChangeInitialized: setInitialized,
    onChangePermissions: setPermissions,
    onFilterItems: filterItems,
  })

  const {
    availableFilters,
    filterValues,
    handleListConfigInit,
    handleSetFilterValues,
    hasFilterChanged,
    isLoading,
    items,
    listLoading,
    ordering,
    pageScrollWrapperRef,
    updateListData,
  } = listPage

  useEffect(() => {
    if (activeTab) {
      // eslint-disable-next-line react-hooks/set-state-in-effect -- applies route/tab state and resets before reload
      setInitialized(false)
      handleListConfigInit()
    }
  }, [activeTab])

  const { canCreate } = useSectionPermissions({
    sectionName: ROUTE_UI_SECTION_NAME.newsboard,
  })

  function handleCloseModal() {
    setModal(null)
  }

  const { handleOpenCreation } = useCreatePost({
    setModal,
    onClose: handleCloseModal,
    onReload: updateListData,
  })

  function handleClickCreatePost() {
    handleOpenCreation()
  }

  const prevTab = usePrevious(activeTab)

  function onShowEmptyList(items) {
    if (prevTab !== activeTab) {
      return false
    }

    return items?.length === 0 && initialized
  }

  const shouldShowEmptyStateActions = canCreate && !hasFilterChanged

  const emptyStateActions = getEmptyStateActions({
    actionName: CREATE_POST_ACTION_NAME,
    isLoading,
    qaName: QA_NAME_POST_ADD_NEW,
    shouldShowActions: shouldShowEmptyStateActions,
    text: t('Newsboard.AddNewBtnKey'),
    onClick: handleClickCreatePost,
  })
  const hasPosts = items?.length > 0

  const hideFiltersBar = !hasPosts && !hasFilterChanged && !listLoading

  return (
    <PostsContextProvider isInitiated={!isLoading || items?.length > 0}>
      <PageWithWidgets
        widgetsFlexible
        className={styles.postsPage}
        qaName={QA_NAME_NEWSBOARD_PAGE}
        scrollWrapperRef={pageScrollWrapperRef}
        widgets={isMobileScreenSize ? null : <PostsWidgetList />}
        widgetsClassName={styles.widgetsWrapper}
      >
        <PostListContainer>
          {isLoading ? <PageLoader /> : null}
          <div className={styles.headerWrapper}>
            <PageHeader title={t(MENU_POST_ITEM.title)}>
              {canCreate && !hideFiltersBar ? (
                <PageActions>
                  <PageActionsButtonAddEntity
                    isSkeleton={isLoading}
                    qaName={QA_NAME_POST_ADD_NEW}
                    text={t('Newsboard.AddNewBtnKey')}
                    onClick={handleClickCreatePost}
                  />
                </PageActions>
              ) : null}
            </PageHeader>
          </div>
          {isMobileScreenSize ? (
            <div className={styles.widgetsWrapper}>
              <PostsWidgetList />
            </div>
          ) : null}
          <TabScrollList
            activeTab={activeTab}
            getTabs={getPostsTabs}
            onSetActiveTab={setActiveTab}
          />
          {availableFilters.length > 0 || listLoading ? (
            <FilterBar
              responsive
              availableFilters={availableFilters}
              canViewFilterTemplate={permissions.can_view_filter_template}
              entity={NEWSBOARD_ENTITY}
              excludeFilters={EXCLUDE_FILTERS}
              filterValues={filterValues}
              getPermanentFilters={getPermanentFilters}
              isHidden={hideFiltersBar}
              isSkeleton={isLoading}
              ordering={ordering}
              parsePermanentFilters={parsePermanentFilters}
              setFilterValues={handleSetFilterValues}
              sub={activeTab}
              onReload={updateListData}
            />
          ) : null}
          <PostListLayout
            emptyStateActions={emptyStateActions}
            getShowEmptyListRules={onShowEmptyList}
            listPage={listPage}
            onHideWelcomePost={handleHideWelcomePost}
          />
        </PostListContainer>
        {modal}
      </PageWithWidgets>
    </PostsContextProvider>
  )
}

export default Posts
